import React, { memo, useMemo, useState } from "react";
import { Modal, Tabs, Avatar, Empty, Spin } from "antd";
import { useGetMessageReactions } from "../../hooks/queries/useGetMessageReactions";
import styles from "./ReactionDetailsModal.module.scss";

interface ReactionDetailsModalProps {
  messageId: string;
  isOpen: boolean;
  onClose: () => void;
}

// Reaction icons và labels
const REACTION_ICONS = [
  "/icons/favorite.png.png",
  "/icons/love.png.png",
  "/icons/care.png.png",
  "/icons/haha.png.png",
  "/icons/wow.png.png",
  "/icons/sad.png.png",
  "/icons/like.png.png",
  "/icons/done.png.png",
];

const REACTION_LABELS = ["Favorite", "Love", "Care", "Haha", "Wow", "Sad", "Like", "Done"];

export const ReactionDetailsModal: React.FC<ReactionDetailsModalProps> = ({ messageId, isOpen, onClose }) => {
  const { data, isLoading } = useGetMessageReactions(messageId, isOpen);
  const [activeTab, setActiveTab] = useState<string>("all");

  const reactions = data?.Data || [];

  // Group reactions by type và count
  const reactionStats = useMemo(() => {
    const stats: Record<number, number> = {};
    reactions.forEach((r) => {
      stats[r.Reaction] = (stats[r.Reaction] || 0) + 1;
    });
    return stats;
  }, [reactions]);

  // Filter reactions based on active tab
  const filteredReactions = useMemo(() => {
    if (activeTab === "all") return reactions;
    const reactionType = parseInt(activeTab);
    return reactions.filter((r) => r.Reaction === reactionType);
  }, [reactions, activeTab]);

  // Build tabs dynamically
  const tabItems = useMemo(() => {
    const items = [
      {
        key: "all",
        label: (
          <div className={styles.tabLabel}>
            <span>Tất cả</span>
            <span className={styles.count}>{reactions.length}</span>
          </div>
        ),
      },
    ];

    // Add tabs for each reaction type that exists
    Object.entries(reactionStats)
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .forEach(([reactionType, count]) => {
        const type = parseInt(reactionType);
        items.push({
          key: reactionType,
          label: (
            <div className={styles.tabLabel}>
              <img src={REACTION_ICONS[type]} alt={REACTION_LABELS[type]} className={styles.tabIcon} />
              <span className={styles.count}>{count}</span>
            </div>
          ),
        });
      });

    return items;
  }, [reactions.length, reactionStats]);

  return (
    <Modal
      title="Biểu cảm"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={460}
      centered
      className={styles.reactionModal}
      bodyStyle={{
        padding: 0,
        maxHeight: "500px",
      }}
    >
      <div className={styles.modalContent}>
        {/* Tabs for filtering */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className={styles.reactionTabs}
          size="small"
        />

        {/* Reaction list */}
        <div className={styles.reactionList}>
          {isLoading ? (
            <div className={styles.loading}>
              <Spin />
              <p>Đang tải...</p>
            </div>
          ) : filteredReactions.length === 0 ? (
            <div className={styles.empty}>
              <Empty description="Chưa có ai thả cảm xúc" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            filteredReactions.map((reaction) => (
              <div key={reaction.Id} className={styles.reactionItem}>
                <Avatar src={reaction.AvatarUrl} size={40} className={styles.avatar}>
                  {!reaction.AvatarUrl && reaction.Creator?.charAt(0).toUpperCase()}
                </Avatar>
                <div className={styles.info}>
                  <div className={styles.name}>{reaction.ContactName || reaction.Creator}</div>
                  <div className={styles.time}>
                    {new Date(reaction.Created).toLocaleString("vi-VN", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <img
                  src={REACTION_ICONS[reaction.Reaction]}
                  alt={REACTION_LABELS[reaction.Reaction]}
                  className={styles.reactionIcon}
                  title={REACTION_LABELS[reaction.Reaction]}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default memo(ReactionDetailsModal);
